import { Phone, MapPin, Package, Printer } from 'lucide-react';
import { Order } from '../types';
import { ResponsiveModal, ModalFooter, ModalButton } from '../../../components/ui/ResponsiveModal';

interface OrderDetailsModalProps {
    order: Order | null;
    onClose: () => void;
    onPrintLabel: (order: Order) => void;
    isPrinting: boolean;
}

export function OrderDetailsModal({ order, onClose, onPrintLabel, isPrinting }: OrderDetailsModalProps) {
    if (!order) return null;

    return (
        <ResponsiveModal
            isOpen={!!order}
            onClose={onClose}
            title={`รายละเอียดคำสั่งซื้อ #${order.orderNumber}`}
            size="lg"
            mobileStyle="fullscreen"
            footer={
                <ModalFooter>
                    <ModalButton variant="secondary" onClick={onClose}>
                        ปิด
                    </ModalButton>
                    {order.status === 'paid' && (
                        <ModalButton
                            variant="primary"
                            onClick={() => {
                                onPrintLabel(order);
                                onClose();
                            }}
                            disabled={isPrinting}
                        >
                            <Printer className="w-4 h-4 inline mr-2" />
                            ปริ้นใบขนส่ง
                        </ModalButton>
                    )}
                </ModalFooter>
            }
        >
            <div className="space-y-6">
                <div>
                    <h4 className="font-medium text-stone-900 dark:text-gray-100 mb-3 text-sm">ข้อมูลลูกค้า</h4>
                    <div className="bg-stone-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                            <span className="font-medium text-stone-600 dark:text-gray-400">ชื่อ:</span>
                            <span className="text-stone-900 dark:text-gray-100">{order.customerName}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                            <Phone className="w-4 h-4 text-stone-500 dark:text-gray-400" />
                            <span className="font-mono text-stone-900 dark:text-gray-100">{order.customerPhone}</span>
                        </div>
                    </div>
                </div>

                {order.shippingAddress && (
                    <div>
                        <h4 className="font-medium text-stone-900 dark:text-white mb-3 text-sm flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            ที่อยู่จัดส่ง
                        </h4>
                        <div className="bg-stone-50 dark:bg-stone-700 rounded-lg p-4 space-y-1 text-sm">
                            <p className="font-medium text-stone-900 dark:text-white">{order.shippingAddress.fullName}</p>
                            <p className="text-stone-600 dark:text-stone-400 font-mono">{order.shippingAddress.phone}</p>
                            <p className="text-stone-700 dark:text-stone-300 leading-relaxed">
                                {order.shippingAddress.address}<br />
                                {order.shippingAddress.district} {order.shippingAddress.province} {order.shippingAddress.postalCode}
                            </p>
                            {order.trackingNumber && (
                                <div className="mt-3 pt-3 border-t border-stone-200 dark:border-stone-600">
                                    <p className="text-sm font-medium text-yellow-700 dark:text-yellow-500 flex items-center gap-2">
                                        <Package className="w-4 h-4" />
                                        เลขติดตาม: <span className="font-mono">{order.trackingNumber}</span>
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div>
                    <h4 className="font-medium text-stone-900 dark:text-white mb-3 text-sm">รายการสินค้า</h4>
                    <div className="space-y-2">
                        {order.items.map((item) => (
                            <div key={item.id} className="flex justify-between items-start bg-stone-50 dark:bg-stone-700 rounded-lg p-3">
                                <div className="flex-1">
                                    <p className="font-medium text-sm text-stone-900 dark:text-white">{item.productName}</p>
                                    {item.variant && (
                                        <p className="text-xs text-stone-600 dark:text-stone-400 mt-0.5">{item.variant}</p>
                                    )}
                                    <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">
                                        จำนวน: <span className="font-mono">{item.quantity}</span> ชิ้น
                                    </p>
                                </div>
                                <div className="text-right ml-4">
                                    <p className="font-mono font-bold text-sm text-emerald-600">
                                        ฿{(item.price * item.quantity).toLocaleString('th-TH')}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-4 pt-4 border-t border-stone-200 dark:border-stone-600">
                        <h4 className="font-medium text-stone-900 dark:text-white mb-3 text-sm">📋 สรุปคำสั่งซื้อ</h4>
                        <div className="bg-stone-50 dark:bg-stone-700 rounded-lg p-4 space-y-2 text-sm">
                            <div className="flex justify-between items-center">
                                <span className="text-stone-600 dark:text-stone-400">ยอดรวมสินค้า ({order.items.length} รายการ):</span>
                                <span className="font-mono text-stone-900 dark:text-white">฿{(order.subtotal ?? 0).toLocaleString('th-TH')}</span>
                            </div>

                            {(order.deliveryDiscountAmount ?? 0) > 0 && (
                                <div className="flex justify-between items-center">
                                    <span className="text-stone-600 dark:text-stone-400">ส่วนลดค่าจัดส่ง:</span>
                                    <span className="font-mono text-red-500">-฿{(order.deliveryDiscountAmount ?? 0).toLocaleString('th-TH')}</span>
                                </div>
                            )}

                            <div className="flex justify-between items-center">
                                <span className="text-stone-600 dark:text-stone-400">ค่าจัดส่ง:</span>
                                <span className={`font-mono ${(order.shippingCost ?? 0) === 0 ? 'text-emerald-600' : 'text-stone-900 dark:text-white'}`}>
                                    {(order.shippingCost ?? 0) === 0 ? 'ฟรี' : `฿${(order.shippingCost ?? 0).toLocaleString('th-TH')}`}
                                </span>
                            </div>

                            {/* Add other discounts from order type */}
                            {(order.secretDealDiscountAmount ?? 0) > 0 && (
                                <div className="flex justify-between items-center">
                                    <span className="text-stone-600 dark:text-stone-400">ส่วนลดดีลลับ:</span>
                                    <span className="font-mono text-red-500">-฿{(order.secretDealDiscountAmount ?? 0).toLocaleString('th-TH')}</span>
                                </div>
                            )}

                            {(order.loyaltyDiscountAmount ?? 0) > 0 && (
                                <div className="flex justify-between items-center">
                                    <span className="text-stone-600 dark:text-stone-400">ส่วนลดสมาชิก:</span>
                                    <span className="font-mono text-red-500">-฿{(order.loyaltyDiscountAmount ?? 0).toLocaleString('th-TH')}</span>
                                </div>
                            )}

                            {(order.paymentFee ?? 0) > 0 && (
                                <div className="flex justify-between items-center border-t border-stone-200 dark:border-stone-600 pt-2">
                                    <span className="text-stone-600 dark:text-stone-400">ค่าธรรมเนียม:</span>
                                    <span className="font-mono text-stone-900 dark:text-white">฿{(order.paymentFee ?? 0).toLocaleString('th-TH')}</span>
                                </div>
                            )}

                            <div className="flex justify-between items-center pt-3 mt-2 border-t border-stone-300 dark:border-stone-500">
                                <span className="font-semibold text-stone-900 dark:text-white">ยอดรวมทั้งสิ้น:</span>
                                <span className="text-xl font-mono font-bold text-emerald-600">
                                    ฿{order.totalAmount.toLocaleString('th-TH')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ResponsiveModal>
    );
}
